import React, { useState } from 'react';
import { useNotificationContext } from '../context/NotificationContext';
import '../styles/PaymentModal.css';

declare global {
  interface Window {
    Razorpay: any;
  }
}

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPaymentSuccess: () => void;
  bookingDetails: {
    bookingId: number;
    amount: number;
    customerName: string;
    customerEmail: string;
    customerPhone: string;
  };
}

const PaymentModal: React.FC<PaymentModalProps> = ({ 
  isOpen, 
  onClose, 
  onPaymentSuccess, 
  bookingDetails 
}) => {
  const { showSuccess, showError } = useNotificationContext();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'online' | 'offline' | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handlePaymentMethodSelection = (method: 'online' | 'offline') => {
    setSelectedPaymentMethod(method);
  };

  const handleOfflinePayment = async () => {
    setIsProcessing(true);
    
    try {
      const response = await fetch('http://localhost:5000/api/v1/payments/confirm-offline-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bookingId: bookingDetails.bookingId,
          paymentMethod: 'offline'
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        showSuccess('Booking confirmed! Payment can be made at the time of consultation.');
        onPaymentSuccess();
      } else {
        showError(result.message || 'Failed to confirm booking');
      }
    } catch (error) {
      console.error('Offline payment error:', error);
      showError('Failed to confirm booking. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleOnlinePayment = async () => {
    setIsProcessing(true);

    try {
      // Load Razorpay script if not already loaded
      if (!window.Razorpay) {
        const loaded = await loadRazorpayScript();
        if (!loaded) {
          showError('Failed to load payment gateway. Please try again.');
          setIsProcessing(false);
          return;
        }
      }

      // Create order on backend
      const orderResponse = await fetch('http://localhost:5000/api/v1/payments/create-order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: bookingDetails.amount * 100, // Convert to paise
          currency: 'INR',
          bookingId: bookingDetails.bookingId,
          customerDetails: {
            name: bookingDetails.customerName,
            email: bookingDetails.customerEmail,
            phone: bookingDetails.customerPhone,
          }
        }),
      });

      const orderData = await orderResponse.json();
      
      if (!orderData.success) {
        throw new Error(orderData.message || 'Failed to create payment order');
      }

      // Initialize Razorpay
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_key', // Replace with your key
        amount: orderData.data.amount,
        currency: orderData.data.currency,
        name: 'WeLegal',
        description: 'Legal Consultation Booking',
        order_id: orderData.data.id,
        handler: async (response: any) => {
          try {
            // Verify payment on backend
            const verifyResponse = await fetch('http://localhost:5000/api/v1/payments/verify-payment', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                bookingId: bookingDetails.bookingId,
              }),
            });

            const verifyResult = await verifyResponse.json();
            
            if (verifyResult.success) {
              showSuccess('Payment successful! Your booking is confirmed.');
              onPaymentSuccess();
            } else {
              showError('Payment verification failed. Please contact support.');
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            showError('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: bookingDetails.customerName,
          email: bookingDetails.customerEmail,
          contact: bookingDetails.customerPhone,
        },
        notes: {
          bookingId: bookingDetails.bookingId.toString(),
        },
        theme: {
          color: '#080838',
        },
        modal: {
          ondismiss: () => {
            setIsProcessing(false);
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (error) {
      console.error('Online payment error:', error);
      showError('Failed to initiate payment. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleConfirmPayment = () => {
    if (selectedPaymentMethod === 'online') {
      handleOnlinePayment();
    } else if (selectedPaymentMethod === 'offline') {
      handleOfflinePayment();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content payment-modal">
        <button className="close-btn" onClick={onClose}>×</button>
        
        <div className="payment-header">
          <div className="payment-icon">
            <i className="fas fa-credit-card"></i>
          </div>
          <h2>Complete Your Payment</h2>
          <p className="payment-description">
            Choose your preferred payment method to confirm your booking
          </p>
        </div>

        <div className="booking-summary">
          <h3>Booking Summary</h3>
          <div className="summary-item">
            <span>Legal Consultation</span>
            <span>₹{bookingDetails.amount}</span>
          </div>
          <div className="summary-item total">
            <span>Total Amount</span>
            <span>₹{bookingDetails.amount}</span>
          </div>
        </div>

        <div className="payment-methods">
          <h3>Select Payment Method</h3>
          
          <div className="payment-options">
            <label 
              className={`payment-option ${selectedPaymentMethod === 'online' ? 'selected' : ''}`}
              onClick={() => handlePaymentMethodSelection('online')}
            >
              <input
                type="radio"
                name="paymentMethod"
                value="online"
                checked={selectedPaymentMethod === 'online'}
                onChange={() => handlePaymentMethodSelection('online')}
              />
              <div className="payment-option-content">
                <div className="payment-option-icon">
                  <i className="fas fa-credit-card"></i>
                </div>
                <div className="payment-option-details">
                  <h4>Online Payment</h4>
                  <p>Pay securely using UPI, Cards, Net Banking</p>
                  <div className="payment-badges">
                    <span className="badge">UPI</span>
                    <span className="badge">Cards</span>
                    <span className="badge">Net Banking</span>
                  </div>
                </div>
              </div>
            </label>

            <label 
              className={`payment-option ${selectedPaymentMethod === 'offline' ? 'selected' : ''}`}
              onClick={() => handlePaymentMethodSelection('offline')}
            >
              <input
                type="radio"
                name="paymentMethod"
                value="offline"
                checked={selectedPaymentMethod === 'offline'}
                onChange={() => handlePaymentMethodSelection('offline')}
              />
              <div className="payment-option-content">
                <div className="payment-option-icon">
                  <i className="fas fa-money-bill-wave"></i>
                </div>
                <div className="payment-option-details">
                  <h4>Pay Later</h4>
                  <p>Pay at the time of consultation</p>
                  <div className="payment-badges">
                    <span className="badge">Cash</span>
                    <span className="badge">Online</span>
                  </div>
                </div>
              </div>
            </label>
          </div>
        </div>

        <div className="payment-actions">
          <button 
            className="btn btn-secondary" 
            onClick={onClose}
            disabled={isProcessing}
          >
            Cancel
          </button>
          <button 
            className={`btn btn-primary ${isProcessing ? 'loading' : ''}`}
            onClick={handleConfirmPayment}
            disabled={!selectedPaymentMethod || isProcessing}
          >
            {isProcessing ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                {selectedPaymentMethod === 'online' ? 'Processing...' : 'Confirming...'}
              </>
            ) : (
              selectedPaymentMethod === 'online' ? 'Pay Now' : 'Confirm Booking'
            )}
          </button>
        </div>

        <div className="payment-security">
          <div className="security-item">
            <i className="fas fa-shield-alt"></i>
            <span>100% Secure Payments</span>
          </div>
          <div className="security-item">
            <i className="fas fa-lock"></i>
            <span>256-bit SSL Encrypted</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;